/*
 * Функции группы на экране — копия правил сервера
 * (server/src/projects/functions.model.ts), как web/src/contracts/registry.ts
 * повторяет правила договоров: экран обязан знать то же, что проверит
 * сервер, и знать это до запроса. Меняется одно — меняется и другое.
 */
import type { ContractType } from '../api';

export const FUNCTIONS = ['OWNER', 'BROKER', 'DESIGN', 'BUILD', 'MANAGE'] as const;
export type FunctionName = (typeof FUNCTIONS)[number];
export const isFunction = (v: unknown): v is FunctionName => (FUNCTIONS as readonly unknown[]).includes(v);

export const FUNCTION_LABEL: Record<FunctionName, string> = {
  OWNER: 'Собственник', BROKER: 'Брокер', DESIGN: 'Генпроектировщик', BUILD: 'Генподрядчик', MANAGE: 'Управление',
};

/** Функция строчными — внутри строки: «Платов · генподрядчик». */
export const functionWord = (fn: FunctionName): string => FUNCTION_LABEL[fn].toLowerCase();

/** Раздел реестра доступа, которым закрыта функция. У генподряда — прежний код проектов. */
export const FUNCTION_SECTION: Record<FunctionName, string> = {
  OWNER: 'section.owners', BROKER: 'section.broker', DESIGN: 'section.design', BUILD: 'section.projects', MANAGE: 'section.manage',
};

/** Префикс этапов работы функции: stage.project.team → stage.design.team. */
export const FUNCTION_STAGE_PREFIX: Record<FunctionName, string> = {
  OWNER: 'stage.owners', BROKER: 'stage.broker', DESIGN: 'stage.design', BUILD: 'stage.project', MANAGE: 'stage.manage',
};

/** Вкладки экрана работы по функции (ProjSection + 'contracts', 'docs' — досье здания у собственника, 'finance' — заявки на оплату работы). */
export type WorkTab = 'overview' | 'docs' | 'gpr' | 'packages' | 'sprints' | 'walks' | 'model' | 'team' | 'contracts' | 'finance' | 'settings';
export const FUNCTION_TABS: Record<FunctionName, WorkTab[]> = {
  OWNER: ['overview', 'docs', 'gpr', 'team', 'contracts', 'finance'],
  BROKER: ['overview', 'team', 'contracts', 'finance'],
  DESIGN: ['overview', 'gpr', 'sprints', 'model', 'team', 'contracts', 'finance'],
  BUILD: ['overview', 'gpr', 'packages', 'sprints', 'walks', 'team', 'contracts', 'finance', 'settings'],
  MANAGE: ['overview', 'team', 'contracts', 'finance'],
};

/** Стандартный договор функции: тип; вид работ — запись классификатора с этой функцией. */
export const FUNCTION_CONTRACT_TYPE: Record<Exclude<FunctionName, 'OWNER'>, ContractType> = {
  BROKER: 'AGENCY', DESIGN: 'PIR', BUILD: 'BUILD', MANAGE: 'SERVICES',
};

/** Порядок вкладок «Функций» (решение Ильи 01.10.2026). */
export const FUNCTION_ORDER_IN_MENU: FunctionName[] = ['OWNER', 'DESIGN', 'BUILD', 'BROKER', 'MANAGE'];

/**
 * Этапы работ функций в реестре сервера (stage.<функция>.X). Экран реестра
 * целиком не получает, поэтому держит их копией; сверку с реестром делает
 * functions.test.ts.
 */
export const FUNCTION_STAGES: readonly string[] = [
  'stage.owners.team', 'stage.owners.gpr', 'stage.broker.team',
  'stage.design.team', 'stage.design.gpr', 'stage.design.sprints', 'stage.manage.team',
  'stage.design.model.upload', 'stage.design.model.remark',
  'stage.owners.finance', 'stage.broker.finance', 'stage.design.finance', 'stage.manage.finance',
];

export const functionStageExists = (code: string): boolean => FUNCTION_STAGES.includes(code);

/** Подразделы генподряда и вкладки, которым они отвечают. */
const BUILD_SUBSECTIONS: Record<string, WorkTab> = {
  'section.projects.gpr': 'gpr',
  'section.projects.packages': 'packages',
  'section.projects.sprints': 'sprints',
  'section.projects.walks': 'walks',
  'section.projects.team': 'team',
  'section.projects.settings': 'settings',
};

/** Этапы генподряда, у которых бывает двойник у другой функции. */
const SHARED_STAGES = ['gpr', 'sprints', 'team', 'finance'];

const BUILD_STAGE = 'stage.project.';

/**
 * Код реестра, которым закрыт маршрут работы этой функции (как у стража
 * сервера): подраздел генподряда — раздел функции целиком, этап
 * stage.project.X — `${prefix}.X`. null — у функции такого нет.
 */
export function codeForFunction(code: string, fn: FunctionName, stageExists: (code: string) => boolean): string | null {
  if (fn === 'BUILD') return code;
  if (code === 'section.projects') return FUNCTION_SECTION[fn];
  const tab = BUILD_SUBSECTIONS[code];
  if (tab) return FUNCTION_TABS[fn].includes(tab) ? FUNCTION_SECTION[fn] : null;
  if (code.startsWith(BUILD_STAGE)) {
    const rest = code.slice(BUILD_STAGE.length);
    if (!SHARED_STAGES.includes(rest)) return null;
    const own = `${FUNCTION_STAGE_PREFIX[fn]}.${rest}`;
    return stageExists(own) ? own : null;
  }
  if (code.startsWith('stage.walk.') || code.startsWith('process.project.') || code === 'process.walk') return null;
  return code;
}

export type WorkState = 'CLOSED' | 'SIGNED' | 'PREPARING' | 'OWN' | 'NONE';

/**
 * Состояние работы: «завершена», «по договору», «договор готовится», «силами
 * собственника» (исполнитель — собственник объекта: договор с самим собой не
 * заводится), «без договора». `ownerId` — юрлицо открытой работы собственника
 * на том же объекте; у самой работы собственника «силами собственника» не бывает.
 */
export function workState(
  w: { closedAt: string | null; baseContract: { status: string } | null; function: FunctionName; legalEntity: { id: string } | null },
  ownerId: string | null,
): WorkState {
  if (w.closedAt) return 'CLOSED';
  if (w.baseContract && w.baseContract.status !== 'CANCELLED') return w.baseContract.status === 'SIGNED' ? 'SIGNED' : 'PREPARING';
  return ownerId && w.function !== 'OWNER' && w.legalEntity?.id === ownerId ? 'OWN' : 'NONE';
}
